// frontend/src/pages/auditoria/AuditoriaGlobal.tsx
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Stack, Alert, Group, MultiSelect, Button, Text, Table, Badge, Code,
} from '@mantine/core'
import { IconAlertOctagon, IconExternalLink } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { DataCard } from '@/components/ui'
import { SeverityBadge } from '@/components/audit/SeverityBadge'
import { ActorCell } from '@/components/audit/ActorCell'
import { OutcomeIcon } from '@/components/audit/OutcomeIcon'

const PAGE_SIZE = 20

interface GlobalAuditResponse {
  items: any[]
  total: number
  tenant_ids: number[]
}

export function AuditoriaGlobal() {
  const [searchParams, setSearchParams] = useSearchParams()
  const resourceType = searchParams.get('resource_type')
  const resourceId = searchParams.get('resource_id')
  const hasDeeplink = !!(resourceType || resourceId)

  const [tenantIds, setTenantIds] = useState<string[]>([])
  const [page, setPage] = useState(1)
  const offset = (page - 1) * PAGE_SIZE

  const { data: tenantsAvail } = useQuery<{ tenant_id: number; tenant_name: string }[]>({
    queryKey: ['tenants-list'],
    queryFn: async () => (await api.get('/v1/tenants')).data.items ?? [],
  })

  const tenantOptions = (tenantsAvail ?? []).map(t => ({ value: String(t.tenant_id), label: t.tenant_name }))

  const { data, isLoading, refetch, isFetching } = useQuery<GlobalAuditResponse>({
    queryKey: ['audit-global', tenantIds, offset, resourceType, resourceId],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) })
      tenantIds.forEach(t => params.append('tenant_ids', t))
      if (resourceType) params.append('resource_type', resourceType)
      if (resourceId) params.append('resource_id', resourceId)
      const res = await api.get<GlobalAuditResponse>(`/v1/audit/global?${params}`)
      return res.data
    },
    enabled: false,  // só dispara on-click p/ não auditar acidentalmente
  })

  // Auto-fire query quando vem por deeplink (Audit button do AppDrawer etc).
  // Sem isso, o usuario veria a tela vazia e teria que clicar Consultar.
  useEffect(() => {
    if (hasDeeplink) refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDeeplink, resourceType, resourceId])

  const handleAthena = () => {
    const url = `https://console.aws.amazon.com/athena/home?region=us-east-1#query`
    window.open(url, '_blank')
  }

  const clearDeeplink = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('resource_type')
    next.delete('resource_id')
    setSearchParams(next)
  }

  return (
    <Stack gap="md">
      <Alert
        icon={<IconAlertOctagon size={20} />}
        color="orange"
        title="Modo Auditor Global"
      >
        Você está no modo Auditor Global. Toda consulta nesta tela gera evento{' '}
        <code>audit:cross_tenant_query</code> em CADA tenant visualizado, com seus
        filtros no payload.
      </Alert>

      {hasDeeplink && (
        <Alert color="blue" variant="light" title="Filtro vindo por deeplink">
          <Group gap="xs" wrap="wrap">
            {resourceType && <Code>resource_type = {resourceType}</Code>}
            {resourceId && <Code>resource_id = {resourceId}</Code>}
            <Button size="compact-xs" variant="subtle" onClick={clearDeeplink}>
              Limpar filtro
            </Button>
          </Group>
        </Alert>
      )}

      <DataCard
        title="Auditoria Global (cross-tenant)"
        accent="warning"
        loading={isLoading} refreshable refreshing={isFetching} onRefresh={() => refetch()}
        extra={
          <Button
            size="xs" variant="light"
            leftSection={<IconExternalLink size={14} />}
            onClick={handleAthena}
          >
            Abrir no Athena (queries pesadas)
          </Button>
        }
      >
        <Stack gap="sm">
          <Group>
            <MultiSelect
              label="Tenants (vazio = todos)"
              data={tenantOptions}
              value={tenantIds} onChange={setTenantIds}
              w={500} searchable clearable
            />
            <Button mt={22} onClick={() => { setPage(1); refetch() }}>Consultar</Button>
          </Group>

          {data && (
            <>
              <Text size="sm" c="dimmed">
                {data.total} eventos em {data.tenant_ids.length} tenant(s)
              </Text>
              <Table striped>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Data</Table.Th>
                    <Table.Th>Sev</Table.Th>
                    <Table.Th>Tenant</Table.Th>
                    <Table.Th>Usuário</Table.Th>
                    <Table.Th>Ação</Table.Th>
                    <Table.Th>Recurso</Table.Th>
                    <Table.Th>Out</Table.Th>
                    <Table.Th>IP</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.items.map((e, i) => (
                    <Table.Tr key={e.id ?? i}>
                      <Table.Td><Text size="xs" ff="monospace">{new Date(e.created_at).toLocaleString('pt-BR')}</Text></Table.Td>
                      <Table.Td><SeverityBadge severity={e.severity} /></Table.Td>
                      <Table.Td><Badge size="xs" variant="outline">{e.tenant_id}</Badge></Table.Td>
                      <Table.Td>
                        <ActorCell actor={{
                          email: e.actor_user_email, name: e.actor_user_name,
                          roles: e.actor_roles, legacy: e.legacy,
                        }} />
                      </Table.Td>
                      <Table.Td><Text size="sm">{e.action}</Text></Table.Td>
                      <Table.Td><Badge size="sm" variant="light">{e.resource_type}</Badge></Table.Td>
                      <Table.Td><OutcomeIcon outcome={e.outcome} /></Table.Td>
                      <Table.Td><Text size="xs" ff="monospace">{e.client_ip ?? '—'}</Text></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </>
          )}
        </Stack>
      </DataCard>
    </Stack>
  )
}
